import React, { useEffect, useMemo, useState } from 'react';
import Modal from './common/Modal';
import { fieldStyles } from '../config/formStyles';
import { theme } from '../config/theme';
import { ROLE_LABELS, ROLES, ROLES_ASIGNABLES, ROLES_DELEGABLES } from '../constants/roles';
import { subscribeComerciales } from '../services/comercialesClientesService';
import {
    actualizarDelegacion,
    asignarRolUsuario,
    isEmailAxonValido,
    quitarRolUsuario,
    subscribeAccesosUsuarios,
    subscribeHistorialRoles,
    subscribeRolesUsuarios,
} from '../services/rolesUsuariosService';
import { armarFilasUsuarios, ORIGEN_ROL, puedeAsignarRol } from '../utils/rolesUsuario';

const styles = {
    intro: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: theme.colors.textSecondary, lineHeight: 1.5 },
    sectionTitle: {
        fontFamily: theme.typography.titleFamily,
        fontSize: '0.8rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.colors.primary,
        margin: '1.5rem 0 0.5rem 0',
    },
    altaForm: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' },
    // Sin alto máximo propio: el modal ya scrollea (en el celular, una lista con
    // scroll dentro de otro scroll mostraba dos filas a la vez).
    list: {
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        backgroundColor: theme.colors.surface,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${theme.colors.border}`,
    },
    rowMain: { display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0, flex: '1 1 260px' },
    rowName: { fontSize: '0.95rem', fontWeight: '600', color: theme.colors.secondary, overflowWrap: 'anywhere' },
    rowMeta: { fontSize: '0.78rem', color: theme.colors.textSecondary, overflowWrap: 'anywhere' },
    rowWarning: { fontSize: '0.78rem', color: '#92400E' },
    rowActions: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
    badge: (origen) => ({
        display: 'inline-block',
        padding: '0.15rem 0.55rem',
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        backgroundColor: origen === ORIGEN_ROL.SIN_ASIGNAR ? '#FEF3C7' : '#EBEDED',
        color: origen === ORIGEN_ROL.SIN_ASIGNAR ? '#92400E' : theme.colors.primary,
    }),
    select: { ...fieldStyles.select, marginTop: 0, padding: '0.35rem 0.5rem', width: 'auto', minWidth: '150px' },
    smallButton: { ...fieldStyles.button, ...fieldStyles.secondaryButton, padding: '0.35rem 0.75rem', fontSize: '0.8rem' },
    empty: { padding: '1.25rem 1rem', textAlign: 'center', fontSize: '0.875rem', color: theme.colors.textSecondary },
    delegacion: { display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.25rem' },
    delegacionLabel: { fontSize: '0.78rem', color: theme.colors.textSecondary },
    pill: (activo) => ({
        fontFamily: theme.typography.fontFamily,
        fontSize: '0.75rem',
        fontWeight: '600',
        padding: '0.3rem 0.6rem',
        minHeight: '30px',
        borderRadius: '999px',
        cursor: 'pointer',
        border: `1px solid ${activo ? theme.colors.primary : theme.colors.border}`,
        backgroundColor: activo ? theme.colors.primary : theme.colors.surface,
        color: activo ? '#F6F7F6' : theme.colors.secondary,
    }),
    historialItem: {
        padding: '0.6rem 1rem',
        borderBottom: `1px solid ${theme.colors.border}`,
        fontSize: '0.82rem',
        color: theme.colors.secondary,
        lineHeight: 1.45,
    },
    feedback: (isError) => ({
        marginTop: '1rem',
        padding: '0.75rem 1rem',
        borderRadius: theme.borderRadius,
        fontSize: '0.85rem',
        backgroundColor: isError ? '#FEE2E2' : '#D1FAE5',
        color: isError ? '#991B1B' : '#065F46',
    }),
};

const ORIGEN_LABELS = {
    [ORIGEN_ROL.DUENO]: 'Dueño',
    [ORIGEN_ROL.ASIGNADO]: 'Asignado',
    [ORIGEN_ROL.SGC]: 'Comercial del SGC',
    [ORIGEN_ROL.SIN_ASIGNAR]: 'Sin rol asignado',
};

const formatFechaHora = (ts) => {
    const date = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    return date.toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        timeZone: 'America/Argentina/Buenos_Aires',
    });
};

const formatFecha = (ts) => (ts ? `último ingreso ${formatFechaHora(ts)}` : 'sin ingresos registrados');

const describirRol = (fila) => {
    if (fila.origen === ORIGEN_ROL.SIN_ASIGNAR) {
        return fila.rolUsado ? `eligió ${ROLE_LABELS[fila.rolUsado]} al entrar` : 'sin rol';
    }
    return ROLE_LABELS[fila.rolEfectivo];
};

/**
 * Panel de roles (admins y delegados). Muestra a todos los que usan la planilla
 * con el rol con el que entran y de dónde sale: dueño, asignado acá, comercial del
 * SGC o sin asignar (los que eligen Trader/Middle Office en la transición).
 * Un admin asigna Trader, Middle Office y Comercial; Admin y las delegaciones los
 * toca solo el dueño; un delegado, solo los roles que le delegaron. Abajo, el
 * historial de cambios. Las reglas de Firestore validan lo mismo.
 */
const UsuariosAdminModal = ({ isOpen, onClose, user, rolUsuario }) => {
    const [roles, setRoles] = useState([]);
    const [accesos, setAccesos] = useState([]);
    const [comerciales, setComerciales] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [alta, setAlta] = useState({ email: '', rol: ROLES.TRADER });
    const [feedback, setFeedback] = useState({ message: '', isError: false });
    const [guardando, setGuardando] = useState(null);
    const [historial, setHistorial] = useState([]);

    useEffect(() => {
        if (!isOpen) return undefined;
        setIsLoading(true);
        const unsubs = [
            subscribeRolesUsuarios((docs) => { setRoles(docs); setIsLoading(false); }, () => setIsLoading(false)),
            subscribeAccesosUsuarios(setAccesos, () => setAccesos([])),
            subscribeComerciales(setComerciales, () => setComerciales([])),
            // Si el historial no se puede leer, el panel sigue sin esa sección.
            subscribeHistorialRoles(setHistorial, () => setHistorial([])),
        ];
        return () => unsubs.forEach(unsub => unsub());
    }, [isOpen]);

    const filas = useMemo(
        () => armarFilasUsuarios({ roles, accesos, comerciales }),
        [roles, accesos, comerciales],
    );
    const sinAsignar = filas.filter(f => f.origen === ORIGEN_ROL.SIN_ASIGNAR && f.ultimoLoginAt).length;

    // Para los permisos, un comercial del SGC sin rol asignado cuenta como Comercial:
    // un delegado solo lo toca si tiene ese rol delegado (igual que las reglas).
    const rolParaPermiso = (fila) => (
        fila?.rolAsignado || (fila?.origen === ORIGEN_ROL.SGC ? ROLES.COMERCIAL : null)
    );
    const opcionesPara = (rolAsignado, email = null, rolPermiso = rolAsignado) => ROLES_ASIGNABLES.filter(rolNuevo =>
        rolNuevo === rolAsignado || puedeAsignarRol({ rolUsuario, rolActual: rolPermiso, rolNuevo, email }));
    const filaAlta = filas.find(f => f.email === alta.email.trim().toLowerCase());
    const opcionesAlta = opcionesPara(null, alta.email, rolParaPermiso(filaAlta));
    const rolAlta = opcionesAlta.includes(alta.rol) ? alta.rol : opcionesAlta[0];
    const esDelegado = !rolUsuario?.esAdmin && (rolUsuario?.asigna || []).length > 0;

    const ejecutar = async (clave, accion, mensajeOk) => {
        setGuardando(clave);
        setFeedback({ message: '', isError: false });
        try {
            await accion();
            setFeedback({ message: mensajeOk, isError: false });
            return true;
        } catch (error) {
            setFeedback({
                isError: true,
                message: error?.code === 'permission-denied'
                    ? 'No tenés permiso para ese cambio (el rol Admin y las delegaciones los maneja solo el dueño).'
                    : (error?.message || 'No se pudo guardar el cambio.'),
            });
            return false;
        } finally {
            setGuardando(null);
        }
    };

    const handleAsignar = (fila, rol) => ejecutar(
        fila.email,
        () => asignarRolUsuario({ admin: user, email: fila.email, rol, nombre: fila.nombre }),
        `${fila.nombre || fila.email}: rol ${ROLE_LABELS[rol]} asignado. Lo toma la próxima vez que entre o recargue la planilla.`,
    );

    const handleQuitar = (fila) => {
        const vuelveA = fila.comercialSgc ? 'Comercial (por el SGC)' : 'elegir Trader o Middle Office al entrar';
        if (!window.confirm(`¿Quitar el rol asignado a ${fila.nombre || fila.email}? Pasa a ${vuelveA}.`)) return;
        ejecutar(
            fila.email,
            () => quitarRolUsuario({ admin: user, rolUsuario: { ...fila.rolDoc, id: fila.email } }),
            `${fila.nombre || fila.email}: rol asignado quitado.`,
        );
    };

    const handleDelegacion = (fila, rol) => {
        const actual = fila.rolDoc?.asigna || [];
        const asigna = ROLES_DELEGABLES.filter(r => (r === rol ? !actual.includes(r) : actual.includes(r)));
        ejecutar(
            fila.email,
            () => actualizarDelegacion({ admin: user, rolUsuario: { ...fila.rolDoc, id: fila.email }, asigna }),
            asigna.length
                ? `${fila.nombre || fila.email} puede asignar: ${asigna.map(r => ROLE_LABELS[r]).join(', ')}.`
                : `${fila.nombre || fila.email} ya no asigna roles.`,
        );
    };

    const handleAlta = (event) => {
        event.preventDefault();
        if (!isEmailAxonValido(alta.email)) {
            setFeedback({ message: 'Ingresá un email @axoncapital.com.ar.', isError: true });
            return;
        }
        if (!rolAlta) {
            setFeedback({ message: 'No tenés roles para asignar a esa persona.', isError: true });
            return;
        }
        const existente = filas.find(f => f.email === alta.email.trim().toLowerCase());
        ejecutar(
            'alta',
            () => asignarRolUsuario({ admin: user, email: alta.email, rol: rolAlta, nombre: existente?.nombre || '' }),
            `${alta.email.trim().toLowerCase()}: rol ${ROLE_LABELS[rolAlta]} asignado.`,
        ).then((ok) => { if (ok) setAlta(prev => ({ ...prev, email: '' })); });
    };

    return (
        <Modal
            isOpen={isOpen}
            title="Usuarios y roles"
            maxWidth="860px"
            footer={(
                <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>
                    Cerrar
                </button>
            )}
        >
            <p style={styles.intro}>
                Cada persona entra con un rol fijo: el que le asignás acá o, si no tiene, <strong>Comercial</strong>
                {' '}cuando figura como empleado comercial en el Sistema de Gestión Comercial. Quien no tiene ninguno
                de los dos todavía elige Trader o Middle Office al entrar (transición): asignale su rol para fijarlo.
                {rolUsuario?.esDueno
                    ? ' Como dueño, sos el único que nombra o quita administradores y el que decide quién más puede asignar roles ("Puede asignar").'
                    : esDelegado
                        ? ` Podés asignar o quitar: ${rolUsuario.asigna.map(r => ROLE_LABELS[r]).join(', ')}.`
                        : ' El rol Admin solo lo asigna el dueño.'}
                {' '}Los productores externos no figuran acá: se manejan desde el panel Productores.
            </p>

            {sinAsignar > 0 && (
                <div style={{ ...styles.feedback(false), backgroundColor: '#FEF3C7', color: '#92400E' }} role="status">
                    {sinAsignar === 1
                        ? '1 persona usa la planilla sin rol asignado.'
                        : `${sinAsignar} personas usan la planilla sin rol asignado.`}
                    {' '}Figuran primero en la lista.
                </div>
            )}

            <div style={styles.sectionTitle}>Asignar rol a otra persona</div>
            <form onSubmit={handleAlta} style={styles.altaForm}>
                <input
                    type="email"
                    placeholder="nombre@axoncapital.com.ar"
                    value={alta.email}
                    onChange={e => setAlta(prev => ({ ...prev, email: e.target.value }))}
                    style={{ ...fieldStyles.input, marginTop: 0, flex: '1 1 240px' }}
                    aria-label="Email de la persona"
                />
                <select
                    value={rolAlta || ''}
                    onChange={e => setAlta(prev => ({ ...prev, rol: e.target.value }))}
                    style={styles.select}
                    aria-label="Rol"
                >
                    {opcionesAlta.map(rol => <option key={rol} value={rol}>{ROLE_LABELS[rol]}</option>)}
                </select>
                <button type="submit" disabled={guardando === 'alta'} style={{ ...fieldStyles.button, padding: '0.5rem 1rem' }}>
                    {guardando === 'alta' ? 'Guardando...' : 'Asignar'}
                </button>
            </form>

            {feedback.message && <div style={styles.feedback(feedback.isError)}>{feedback.message}</div>}

            <div style={styles.sectionTitle}>Personas</div>
            <div style={styles.list}>
                {isLoading && <div style={styles.empty}>Cargando usuarios...</div>}
                {!isLoading && filas.map(fila => {
                    const esDueno = fila.origen === ORIGEN_ROL.DUENO;
                    const opciones = opcionesPara(fila.rolAsignado, fila.email, rolParaPermiso(fila));
                    const puedeCambiar = !esDueno && opciones.some(rol => rol !== fila.rolAsignado);
                    const puedeQuitar = !esDueno && fila.rolAsignado
                        && puedeAsignarRol({ rolUsuario, rolActual: fila.rolAsignado, rolNuevo: null, email: fila.email });
                    // Delegar tiene sentido para quien no es admin (un admin ya asigna todo).
                    const puedeDelegar = rolUsuario?.esDueno && fila.rolAsignado && fila.rolAsignado !== ROLES.ADMIN;
                    const delegados = fila.rolDoc?.asigna || [];
                    return (
                        <div key={fila.email} style={styles.row}>
                            <div style={styles.rowMain}>
                                <span style={styles.rowName}>{fila.nombre || fila.email}</span>
                                <span style={styles.rowMeta}>
                                    {fila.email} · {describirRol(fila)} · {formatFecha(fila.ultimoLoginAt)}
                                </span>
                                {fila.rolAsignado && fila.comercialSgc && fila.rolAsignado !== ROLES.COMERCIAL && (
                                    <span style={styles.rowWarning}>
                                        Figura como comercial en el SGC, pero entra como {ROLE_LABELS[fila.rolAsignado]} por el rol asignado.
                                    </span>
                                )}
                                {puedeDelegar && (
                                    <div style={styles.delegacion}>
                                        <span style={styles.delegacionLabel}>Puede asignar:</span>
                                        {ROLES_DELEGABLES.map(rol => (
                                            <button
                                                key={rol}
                                                type="button"
                                                aria-pressed={delegados.includes(rol)}
                                                disabled={guardando === fila.email}
                                                onClick={() => handleDelegacion(fila, rol)}
                                                style={styles.pill(delegados.includes(rol))}
                                            >
                                                {ROLE_LABELS[rol]}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!puedeDelegar && delegados.length > 0 && fila.rolAsignado && (
                                    <span style={styles.rowMeta}>
                                        Puede asignar: {delegados.map(r => ROLE_LABELS[r]).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div style={styles.rowActions}>
                                <span style={styles.badge(fila.origen)}>{ORIGEN_LABELS[fila.origen]}</span>
                                {puedeCambiar && (
                                    <select
                                        value={fila.rolAsignado || ''}
                                        disabled={guardando === fila.email}
                                        onChange={e => e.target.value && handleAsignar(fila, e.target.value)}
                                        style={styles.select}
                                        aria-label={`Rol de ${fila.nombre || fila.email}`}
                                    >
                                        {!fila.rolAsignado && <option value="">Asignar rol...</option>}
                                        {opciones.map(rol => <option key={rol} value={rol}>{ROLE_LABELS[rol]}</option>)}
                                    </select>
                                )}
                                {puedeQuitar && (
                                    <button
                                        type="button"
                                        onClick={() => handleQuitar(fila)}
                                        disabled={guardando === fila.email}
                                        style={styles.smallButton}
                                    >
                                        Quitar rol
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={styles.sectionTitle}>Historial de cambios</div>
            <div style={styles.list}>
                {historial.length === 0 && <div style={styles.empty}>Todavía no hay cambios registrados.</div>}
                {historial.map(cambio => (
                    <div key={cambio.id} style={styles.historialItem}>
                        <strong>{cambio.email}</strong>: {cambio.descripcion}
                        <div style={styles.rowMeta}>
                            {cambio.at ? formatFechaHora(cambio.at) : 'recién'}
                            {' · '}por {cambio.porNombre ? `${cambio.porNombre} (${cambio.por})` : (cambio.por || 'sistema')}
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
};

export default UsuariosAdminModal;
